// gym/GymCoaches.jsx - Version complète corrigée
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,  Alert, Snackbar, Chip, 
  FormControl, InputLabel, Select, MenuItem,
  Tooltip, Card, CardContent, Avatar,
  CircularProgress, InputAdornment
} from '@mui/material';
// MUI v7 : le `Grid` par défaut a perdu les props `item` / `xs` / `md`
// au profit de `size={{...}}`. Ce fichier utilise l'ancienne API.
import { GridLegacy as Grid } from '@mui/material';
import { 
  Add, Edit, Delete, FitnessCenter, CheckCircle, 
  Cancel, Search, Refresh, Person,
  Phone, Email, Work, Payment, CalendarToday,
  Euro
} from '@mui/icons-material';
import api from '../services/auth';

const GymCoaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [filteredCoaches, setFilteredCoaches] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [specialtyFilter, setSpecialtyFilter] = useState('all');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    speciality: 'GENERAL',
    rate_paid: '0.00',
    hired_date: new Date().toISOString().split('T')[0],
    is_active: true
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const SPECIALITY_CHOICES = [
    { value: 'CARDIO', label: 'Cardio' },
    { value: 'MUSCULATION', label: 'Musculation' },
    { value: 'YOGA', label: 'Yoga' },
    { value: 'GENERAL', label: 'Général' }
  ];

  useEffect(() => {
    fetchCoaches();
  }, []);

  useEffect(() => {
    filterCoaches();
  }, [coaches, searchTerm, statusFilter, specialtyFilter]);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching coaches from API...');
      
      const response = await api.get('/gym/coaches/');
      
      console.log('✅ API Response status:', response.status);
      console.log('📊 API Response data:', response.data);
      
      let coachesData = [];
      
      if (Array.isArray(response.data)) {
        coachesData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        if (response.data.results) {
          // Pagination Django REST framework
          coachesData = response.data.results;
          console.log('📄 Pagination detected, total:', response.data.count);
        } else {
          coachesData = Object.values(response.data);
        }
      }
      
      console.log('👥 Processed coaches data:', coachesData);
      console.log('👤 Number of coaches:', coachesData.length);
      
      setCoaches(coachesData);
      
    } catch (error) {
      console.error('❌ Error fetching coaches:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Erreur lors du chargement des coachs', 
        severity: 'error' 
      });
      setCoaches([]);
    } finally {
      setLoading(false);
    }
  };

  const filterCoaches = () => {
    const coachesArray = Array.isArray(coaches) ? coaches : [];
    let filtered = coachesArray;
    
    if (searchTerm) {
      filtered = filtered.filter(coach =>
        (coach.first_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (coach.last_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (coach.email?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (coach.phone?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (coach.code?.toLowerCase() || '').includes(searchTerm.toLowerCase())
      );
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(coach => 
        statusFilter === 'active' ? coach.is_active : !coach.is_active
      );
    }
    
    if (specialtyFilter !== 'all') {
      filtered = filtered.filter(coach => 
        coach.speciality === specialtyFilter
      );
    }
    
    setFilteredCoaches(filtered);
  };

  const handleOpenDialog = (coach = null) => {
    if (coach) {
      console.log('✏️ Opening dialog to edit coach:', coach);
      setEditingCoach(coach);
      setFormData({
        first_name: coach.first_name || '',
        last_name: coach.last_name || '',
        phone: coach.phone || '',
        email: coach.email || '',
        speciality: coach.speciality || 'GENERAL',
        rate_paid: coach.rate_paid || '0.00',
        hired_date: coach.hired_date || new Date().toISOString().split('T')[0],
        is_active: coach.is_active !== undefined ? coach.is_active : true
      });
    } else {
      console.log('➕ Opening dialog for new coach');
      setEditingCoach(null);
      setFormData({
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        speciality: 'GENERAL',
        rate_paid: '0.00',
        hired_date: new Date().toISOString().split('T')[0],
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCoach(null);
    setCreating(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      // Préparer les données pour l'API
      const apiData = {
        ...formData,
        rate_paid: parseFloat(formData.rate_paid) || 0
      };
      
      console.log('🚀 Submitting coach data:', apiData);
      
      if (editingCoach) {
        console.log('📝 Updating existing coach:', editingCoach.id);
        const response = await api.put(`/gym/coaches/${editingCoach.id}/`, apiData);
        console.log('✅ Update response:', response.data);
        setSnackbar({ open: true, message: 'Coach modifié avec succès', severity: 'success' });
      } else {
        console.log('🆕 Creating new coach');
        const response = await api.post('/gym/coaches/', apiData);
        console.log('✅ Create response:', response.data);
        console.log('✅ Response status:', response.status);
        setSnackbar({ open: true, message: 'Coach créé avec succès', severity: 'success' });
      }
      
      handleCloseDialog();
      
      // Rafraîchir la liste après un court délai
      setTimeout(() => {
        fetchCoaches();
      }, 800);
      
    } catch (error) {
      console.error('❌ Error submitting coach:', error);
      console.error('🔍 Error details:', error.response?.data);
      
      let errorMessage = 'Erreur lors de l\'opération';
      if (error.response?.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else {
          // Aplatir toutes les erreurs
          const errors = Object.values(error.response.data).flat();
          errorMessage = errors.join(', ');
        }
      }
      
      setSnackbar({ 
        open: true, 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce coach?')) {
      try {
        console.log('🗑️ Deleting coach:', id);
        await api.delete(`/gym/coaches/${id}/`);
        setSnackbar({ open: true, message: 'Coach supprimé avec succès', severity: 'success' });
        fetchCoaches();
      } catch (error) {
        console.error('Error deleting coach:', error);
        setSnackbar({ open: true, message: 'Erreur lors de la suppression', severity: 'error' });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR');
    } catch (e) {
      return 'N/A';
    }
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const getSpecialityLabel = (value) => {
    const choice = SPECIALITY_CHOICES.find(c => c.value === value);
    return choice ? choice.label : value;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Calcul des statistiques
  const stats = {
    total: Array.isArray(coaches) ? coaches.length : 0,
    active: Array.isArray(coaches) ? coaches.filter(c => c.is_active).length : 0,
    inactive: Array.isArray(coaches) ? coaches.filter(c => !c.is_active).length : 0
  };

  return (
    <Box p={3}>
      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <FitnessCenter color="primary" />
                <Typography variant="h6">Total Coachs</Typography>
              </Box>
              <Typography variant="h4" color="primary.main">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: 'success.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <CheckCircle />
                <Typography variant="h6">Coachs Actifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.active}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ backgroundColor: 'error.light', color: 'white' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <Cancel />
                <Typography variant="h6">Coachs Inactifs</Typography>
              </Box>
              <Typography variant="h4">
                {stats.inactive}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🏋️‍♂️ Coachs GYM</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={creating}
        >
          {creating ? <CircularProgress size={24} /> : 'Nouveau Coach'}
        </Button>
      </Box>

      {/* Barre de recherche et filtres */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Rechercher par nom, code, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Statut</InputLabel>
              <Select
                value={statusFilter}
                label="Statut"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="active">Actifs</MenuItem>
                <MenuItem value="inactive">Inactifs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Spécialité</InputLabel>
              <Select
                value={specialtyFilter}
                label="Spécialité"
                onChange={(e) => setSpecialtyFilter(e.target.value)}
              >
                <MenuItem value="all">Toutes</MenuItem>
                {SPECIALITY_CHOICES.map(spec => (
                  <MenuItem key={spec.value} value={spec.value}>
                    {spec.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchCoaches}
              disabled={loading}
            >
              {loading ? <CircularProgress size={20} /> : 'Actualiser'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tableau */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.light' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Code</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Coach</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Spécialité</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contact</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tarif Horaire</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date embauche</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Statut</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>Chargement des coachs...</Typography>
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredCoaches) || filteredCoaches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  {!Array.isArray(coaches) || coaches.length === 0 ? (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucun coach enregistré
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Cliquez sur "Nouveau Coach" pour ajouter votre premier coach.
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="h6" color="textSecondary" gutterBottom>
                        Aucun coach trouvé avec les filtres actuels
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Essayez de modifier vos critères de recherche.
                      </Typography>
                      <Button 
                        variant="outlined" 
                        sx={{ mt: 2 }}
                        onClick={() => {
                          setSearchTerm('');
                          setStatusFilter('all');
                          setSpecialtyFilter('all');
                        }}
                      >
                        Réinitialiser les filtres
                      </Button>
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredCoaches.map((coach) => (
                <TableRow key={coach.id} hover>
                  <TableCell>
                    <Chip 
                      label={coach.code || 'N/A'} 
                      color="primary" 
                      size="small" 
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {getInitials(coach.first_name, coach.last_name)}
                      </Avatar>
                      <Box>
                        <Typography fontWeight="bold">
                          {coach.first_name} {coach.last_name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          ID: {coach.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getSpecialityLabel(coach.speciality)} 
                      color="primary" 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {coach.email && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Email fontSize="small" color="action" />
                          <Typography variant="body2">{coach.email}</Typography>
                        </Box>
                      )}
                      {coach.phone && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Phone fontSize="small" color="action" />
                          <Typography variant="body2">{coach.phone}</Typography>
                        </Box>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Euro fontSize="small" color="action" />
                      <Typography fontWeight="bold">
                        {formatCurrency(coach.rate_paid)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography>
                        {formatDate(coach.hired_date)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={coach.is_active ? 'Actif' : 'Inactif'}
                      color={coach.is_active ? 'success' : 'error'}
                      size="small"
                      icon={coach.is_active ? <CheckCircle /> : <Cancel />}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      <Tooltip title="Modifier">
                        <IconButton 
                          color="primary" 
                          size="small"
                          onClick={() => handleOpenDialog(coach)}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Supprimer">
                        <IconButton 
                          color="error" 
                          size="small"
                          onClick={() => handleDelete(coach.id)}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog pour créer/modifier */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingCoach ? '✏️ Modifier Coach' : '➕ Nouveau Coach'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prénom *"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  required
                  margin="normal"
                  error={!formData.first_name}
                  helperText={!formData.first_name ? "Ce champ est requis" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nom *"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  required
                  margin="normal"
                  error={!formData.last_name}
                  helperText={!formData.last_name ? "Ce champ est requis" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Téléphone *"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  margin="normal"
                  error={!formData.phone}
                  helperText={!formData.phone ? "Ce champ est requis" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal" required>
                  <InputLabel>Spécialité *</InputLabel>
                  <Select
                    value={formData.speciality}
                    label="Spécialité *"
                    onChange={(e) => setFormData({...formData, speciality: e.target.value})}
                    error={!formData.speciality}
                  >
                    {SPECIALITY_CHOICES.map(spec => (
                      <MenuItem key={spec.value} value={spec.value}>
                        {spec.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Tarif horaire *"
                  type="number"
                  value={formData.rate_paid}
                  onChange={(e) => setFormData({...formData, rate_paid: e.target.value})}
                  required
                  margin="normal"
                  inputProps={{ 
                    min: "0.01", 
                    step: "0.01",
                    onInput: (e) => {
                      e.target.value = Math.max(0, parseFloat(e.target.value)).toString();
                    }
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"></InputAdornment>,
                  }}
                  error={!formData.rate_paid || parseFloat(formData.rate_paid) <= 0}
                  helperText={!formData.rate_paid || parseFloat(formData.rate_paid) <= 0 ? "Doit être supérieur à 0" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Date d'embauche *"
                  type="date"
                  value={formData.hired_date}
                  onChange={(e) => setFormData({...formData, hired_date: e.target.value})}
                  InputLabelProps={{ shrink: true }}
                  required
                  margin="normal"
                  error={!formData.hired_date}
                  helperText={!formData.hired_date ? "Ce champ est requis" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={formData.is_active}
                    label="Statut"
                    onChange={(e) => setFormData({...formData, is_active: e.target.value})}
                  >
                    <MenuItem value={true}>Actif</MenuItem>
                    <MenuItem value={false}>Inactif</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={creating}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={creating || !formData.first_name || !formData.last_name || !formData.phone || !formData.speciality || !formData.rate_paid || !formData.hired_date}
            >
              {creating ? <CircularProgress size={24} /> : editingCoach ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GymCoaches;